import {OrbitComponent} from "../base-components/orbit-component/orbit-component";
import * as React from "react";
import {FreeToPlayAnimatedIconBox} from "./boxes/free-to-play-animated-icon.box";
import {AnimatedIconObservableTarget, AnimatedIconUnloadReason, IAnimatedIconRequestOptions} from "../../services/pixi-app/animated-icon-base-service.service";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../common/const/casinofied-analytics-consts";
import {AreaType, CasinoFreeToPlayGameStage} from "@sparkware/uc-sdk-core";
import {observer} from "mobx-react";
import {CyClass} from "../../../common/cy-classes";
import {ISendElasticEventOptions} from "../../services/analytics/analytics.service.interface";
import {FreeToPlayClickBox} from "./boxes/free-to-play-click.box";
import {ComponentServicesPick} from "../../services/component-services-pick";
import {withServices} from "../../react-hooks/with-services";
import {FreeToPlayIconPositionBox} from "./boxes/free-to-play-icon-position.box";


interface FreeToPlayAnimatedIconComponentProps extends ComponentServicesPick<'bottomNavigationBar' | 'device'> {

}

const ComponentBody = observer(class FreeToPlayAnimatedIconComponent extends OrbitComponent<FreeToPlayAnimatedIconComponentProps> {
    private iconRequestOptions: Omit<IAnimatedIconRequestOptions, "wrapper"> = {
        timings: {
            delay: 2000,
            interval: 3000
        },
        ...this.services.theme.themeCustomizer.getFreeToPlayAnimation(),
    };

    private iconObservableTarget: AnimatedIconObservableTarget | undefined;

    private _captureElementRefForIcon(element: HTMLElement | null) {
        // if element is null (the component was unmounted) or the element changed, then check if we have a currently running/loading animation and remove it
        if (!element || (this.iconObservableTarget && this.iconObservableTarget.wrapper !== element)) {
            // if the current animation is loading, cancel it
            // if the current animation is already running, stop and dispose it
            // animation disposer do both things, abort if loading, dispose if running
            if (this.iconObservableTarget && this.iconObservableTarget.animationDisposer) {
                this.iconObservableTarget.animationDisposer(AnimatedIconUnloadReason.REQUESTED);
            }
        }
        // if the element exists and is different from the previous one, then the animation must be started
        if (element && (!this.iconObservableTarget || this.iconObservableTarget.wrapper !== element)) {
            let requestOptions: Omit<IAnimatedIconRequestOptions, "wrapper"> = this.iconRequestOptions;
            // update the reference
            this.iconObservableTarget = {
                ...requestOptions,
                wrapper: element
            };
            this.services.freeToPlay.loadFreeToPlayAnimation(this.iconObservableTarget);
        }
        // if element is null just update the reference so we know next time the capture is ran
        if (!element) {
            this.iconObservableTarget = undefined;
        }
    }

    componentDidUpdate() {
        if (this.services.freeToPlay.isPromotionEligible) {
            this._sendAnalytics("SHOW");
        }
    }

    render() {
        if (!this.services.freeToPlay.isPromotionEligible) {
            return null;
        }

        // Hide UI of the widget but keep Functionality
        if (!this.services.freeToPlay.shouldShowIcon) {
            return null;
        }

        if (this.services.freeToPlay.isPromotionOpened) {
            return null;
        }

        // On phones with rounded corners and where the icon is right at the corner the icon is a little cut out.
        // This cuts the text inside the icon in MRG. Not a problem on 888 casino skin
        // This issue doesn't happen on iOS because of the safe area bottom reserved for the navigation bar.
        // It also doesn't happen on webapp because there are the system buttons and the browser
        // So, only on Android hybrid (full screen) where there is no bottom nav bar
        // I will pass shouldOffsetIcon and will do nothing with it in default.box and use it only in mrgreen.box
        // This solution is kind of shit because we cannot detect if a screen has rounded corners but since almost all phones since 2017 have rounded corners we just assume that it's a de facto
        let shouldOffsetIcon = false;
        if (this.props.bottomNavigationBar.bottomNavbarHeight === 0 && this.props.device.hybrid.isHybrid && this.props.device.os.isAndroid) {
            shouldOffsetIcon = true;
        }

        return (
            <FreeToPlayIconPositionBox bottomNavbarHeight={this.props.bottomNavigationBar.bottomNavbarHeight} shouldOffsetIcon={shouldOffsetIcon}>
                <FreeToPlayAnimatedIconBox
                    ref={this._captureElementRefForIcon.bind(this)}
                />
                <FreeToPlayClickBox className={CyClass('cy-free-to-play-click-box')} onClick={() => this._openBonusWheel()} />
            </FreeToPlayIconPositionBox>
        );
    }

    private _openBonusWheel = async () => {
        this._sendAnalytics("CLICK");

        // For Italy there will be an AB test. Some users will see the MiniArena as before and some will go directly to the Daily Wish wheel.
        // To show the daily wish directly, channel.element must be "deep_link", otherwise it will not work.
        // Even if it is a bit strange to set the element to "deep_link" instead of "floating_icon" even
        // if the flow does not originate from a deeplink but from a click on the floating icon, I must do this to work
        const gameStage = this.services.abTestingFeatureDataProvider.freeToPlayFlow.isEnabled ? CasinoFreeToPlayGameStage.DailyWish : CasinoFreeToPlayGameStage.MiniArena;
        const element = this.services.abTestingFeatureDataProvider.freeToPlayFlow.isEnabled ? CHANNEL_ELEMENTS.deepLink : CHANNEL_ELEMENTS.floatingIcon;
        
        await this.services.freeToPlay.showPromotion({
            channel: {
                area: AreaType.button,
                source: CHANNEL_SOURCE.casinoClient,
                element
            },
            appSpecificParameters: {
                gameStage
            }
        }
        );
    }

    private _sendAnalytics(action: string) {
        try {
            let options: ISendElasticEventOptions = {
                action: action,
                category: "Floating_icon",
                eventType: "bonus_widget",
                appID: "F2P",
                customData: {
                    location: this.services.location.isHomePage ? 'homepage' : this.services.location.currentUri.pathName.toString(),
                    segmentation: this.services.user.playerRMSStatus
                }
            };
            this.services.analytics.sendElasticInfoEvent(options, true);
        }
        catch (err) {
            console.log("Floating_icon " + action + " analytics report failed with error: " + err);
        }
    }
});

export const FreeToPlayAnimatedIconComponent = withServices(ComponentBody, ['bottomNavigationBar', 'device']);
